import {
  fnMostrarTipoTarjeta,
  gridSizeMdLg12,
  gridSizeMdLg6,
  texto,
} from "@/shared";
import {
  CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomTextFieldNumberComponente,
  SampleDatePicker,
  TabTextLabel,
} from "@/shared/components/ui";
import { TiposTextFieldNumberEnum } from "@/shared/components/ui/CustomTextFieldNumberComponente";
import {
  Contrato,
  Cooperativa,
  EntidadesFinancieras,
  IContentUpdateClientFormType,
  IPaymentMethodV2,
  TIPO_PAGO_V2_OPTIONS,
  TipoPagoV2Enum,
} from "@/shared/interfaces";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import { UseFormReturn } from "react-hook-form";
import { useFormaPago } from "./useFormaPago";
import ImagesWithZoom from "@/shared/components/ui/Images/ImagesWithZoom";

interface DatosFormaPagoProps {
  contract?: Contrato;
  form: UseFormReturn<IContentUpdateClientFormType> | any;
  disabledTipoPago?: boolean;
}

export default function DatosFormaPago({
  contract,
  form,
  disabledTipoPago = false,
}: DatosFormaPagoProps) {
  ///* State
  // const [tipoTarjeta, setTipoTarjeta] = useState("");

  ///* Form
  // const {
  //   reset,
  //   control,
  //   setError,
  //   clearErrors,
  //   formState: { errors },
  // } = form;

  const {
    control,
    watch,
    formState: { errors },
  } = form;

  // Watch para hacer reactivos los campos condicionales
  const tipoPagoV2 = watch('tipo_pago_v2');
  const entidadFinanciera = watch('entidad_financiera');
  const formaPagoV2 = watch('forma_pago_v2');

  const {
    dataCooperativa,
    isLoadingCooperativa,
    handleValidarTarjeta,
    tipoTarjeta,
    handleTipoPagoV2Change,
    handleEntidadV2Change,
    handleFormaPagoV2Change,
    entidadesV2Filtradas,
    isLoadingEntidadesV2,
    formasPagoV2Filtradas,
    isLoadingFormasPagoV2
  } = useFormaPago({
    form:form, 
    formaPagoDataContainer:{
      ...contract?.preventa_data,
      forma_pago_data: contract?.forma_pago_data,
    }
  })



  ///* React Query
  // const { data: paymentMethodsRes, isLoading: isLoadingPayment } =
  //   useFetchPaymentMethodsParams({
  //     page_size: 100,
  //     filterByState: true,
  //   });

  // const { data: dataCooperativa, isLoading: isLoadingCooperativa } =
  //   useFetchCooperativa({
  //     params: {
  //       entidad_financiera: form.watch("entidad_financiera")!,
  //       estado: true,
  //     },
  //     enabled: !!form.watch("entidad_financiera"),
  //   });

  return (
    <>
      <Grid container item {...gridSizeMdLg12}>
        <TabTextLabel
          text={`Entidad Financiera: ${form.getValues().entidad_financiera}`}
        />
      </Grid>


      {/* ============ NIVEL 1: Tipo de Pago (DÉBITO/CRÉDITO) ============ */}
      <CustomAutocompleteArrString
        size={gridSizeMdLg6}
        label="Tipo de Pago"
        name="tipo_pago_v2"
        options={TIPO_PAGO_V2_OPTIONS}
        defaultValue={form.getValues().tipo_pago_v2 || ""}
        isLoadingData={false}
        control={control}
        error={errors.tipo_pago_v2}
        helperText={errors.tipo_pago_v2?.message}
        onChangeValue={(e) => handleTipoPagoV2Change(e as TipoPagoV2Enum)}
        disabled={disabledTipoPago}
      />

      {/* ============ NIVEL 2: Entidad Bancaria (filtrada por tipo de pago) - No aplica para RECAUDACIÓN ============ */}
      {tipoPagoV2 && tipoPagoV2 !== TipoPagoV2Enum.RECAUDACION && (
        <CustomAutocomplete<EntidadesFinancieras>
          key={`entidad-${tipoPagoV2}`}
          textFieldKey={`entidad-field-${tipoPagoV2}`} // Forzar re-render del Controller interno
          size={gridSizeMdLg6}
          label="Entidad Bancaria"
          name="entidad_financiera"
          options={entidadesV2Filtradas || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().entidad_financiera || ""}
          optionLabelForEdit={form.getValues().entidad_financiera || ""}
          isLoadingData={isLoadingEntidadesV2}
          control={control}
          error={errors.entidad_financiera}
          helperText={errors.entidad_financiera?.message}
          onChangeValue={(e) => handleEntidadV2Change(e)}
        />
      )}

      {/* ============ NIVEL 3: Forma de Pago V2 (filtrada por entidad + tipo) - No aplica para RECAUDACIÓN ============ */}
      {tipoPagoV2 && tipoPagoV2 !== TipoPagoV2Enum.RECAUDACION && entidadFinanciera && (
        <CustomAutocomplete<IPaymentMethodV2>
          key={`forma-pago-${tipoPagoV2}-${entidadFinanciera}`}
          textFieldKey={`forma-pago-field-${tipoPagoV2}-${entidadFinanciera}`} // Forzar re-render del Controller interno
          size={gridSizeMdLg6}
          label="Forma de Pago"
          name="forma_pago_v2"
          options={formasPagoV2Filtradas || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().forma_pago_v2 || ""}
          optionLabelForEdit={formasPagoV2Filtradas?.find(fp => fp.codigo === form.getValues().forma_pago_v2)?.nombre || ""}
          isLoadingData={isLoadingFormasPagoV2}
          control={control}
          error={errors.forma_pago_v2}
          helperText={errors.forma_pago_v2?.message}
          onChangeRawValue={(e) => handleFormaPagoV2Change(e)}
        />
      )}

      {/* ============ Campos condicionales según tipo de pago V2 ============ */}
      {tipoPagoV2 === TipoPagoV2Enum.DEBITO && formaPagoV2 && (
        <>
          {/* Campo "Tipo de Cuenta" oculto - se setea automáticamente desde handleFormaPagoV2Change
              basándose en el nombre de "Forma de Pago":
              - "Cuenta Amiga" → "AMIGA"
              - "Cuenta Corriente" → "CORRIENTE"
              - "Cuenta Ahorros" → "AHORRO"
          */}

          {!isLoadingCooperativa && dataCooperativa?.length != 0 && (
            <CustomAutocomplete<Cooperativa>
              size={gridSizeMdLg6}
              label={texto.cooperativa}
              name="cooperativa"
              // options
              options={dataCooperativa || []}
              valueKey="nombre" // name is the fk
              actualValueKey="codigo"
              defaultValue={form.getValues().cooperativa || ""}
              isLoadingData={isLoadingCooperativa}
              // validation
              control={control}
              error={errors.cooperativa}
              helperText={errors.cooperativa?.message}
            /* onChangeValue={(e) => {
                reset({ ...form.getValues(), cooperativa: e });
              }} */
            />
          )}

          <CustomTextFieldNumberComponente
            type={TiposTextFieldNumberEnum.TARJETA}
            size={gridSizeMdLg12}
            label={texto.numero_cuenta}
            error={errors.numero_cuenta}
            helperText={errors.numero_cuenta?.message}
            control={form.control}
            name="numero_cuenta"
            defaultValue={form.getValues().numero_cuenta}
            max={25}
          />
        </>
      )}

      {/* ============ Campos para CRÉDITO (tarjeta) ============ */}
      {tipoPagoV2 === TipoPagoV2Enum.CREDITO && formaPagoV2 && (
        <>
          <SampleDatePicker
            format="YYYY-MM"
            views={["year", "month"]}
            label="Fecha Caducidad *"
            name="tarjeta_fecha_caducidad"
            minDate={dayjs(new Date())}
            control={form.control}
            defaultValue={form.getValues().tarjeta_fecha_caducidad ?? ""}
            error={errors.tarjeta_fecha_caducidad}
            helperText={errors.tarjeta_fecha_caducidad?.message}
            size={gridSizeMdLg6}
          />

          <CustomTextFieldNumberComponente
            type={TiposTextFieldNumberEnum.TARJETA}
            startAdornment={
              fnMostrarTipoTarjeta(tipoTarjeta) ? (
                <img
                  src={fnMostrarTipoTarjeta(tipoTarjeta)}
                  alt="imge-tarjeta"
                  width={"50px"}
                />
              ) : null
            }
            size={gridSizeMdLg6}
            label={texto.ingresar_num_tarjeta}
            error={errors.numero_tarjeta}
            helperText={errors.numero_tarjeta?.message}
            control={form.control}
            name="numero_tarjeta"
            defaultValue={form.getValues().numero_tarjeta ?? ""}
            max={24}
            onChangeValue={(e) => handleValidarTarjeta(e)}
          />
        </>
      )}


      {contract?.preventa_data?.url_foto_cuenta_bancaria ? (
        <Grid container item justifyContent={"center"} {...gridSizeMdLg12}>
          <TabTextLabel text="Foto de Respaldo Cuenta Bancaria" />
          <ImagesWithZoom
            src={`${contract?.preventa_data
              ?.url_foto_cuenta_bancaria!}?v=${new Date().getTime()}`}
            alt={"Foto de Respaldo Cuenta Bancaria"}
          />
        </Grid>
      ) : null}
      {contract?.preventa_data?.url_foto_tarjeta ? (
        <Grid container item justifyContent={"center"} {...gridSizeMdLg12}>
          <TabTextLabel text="Foto de Respaldo Tarjeta" />
          <ImagesWithZoom
            src={`${contract?.preventa_data
              ?.url_foto_tarjeta!}?v=${new Date().getTime()}`}
            alt={"Foto de Respaldo Tarjeta"}
          />
        </Grid>
      ) : null}
    </>
  );
}
